/**
 * Fire-and-forget `component.viewed` emission to the admin's storefront
 * ingest endpoint. Runs once per bundle boot when the embed is a real
 * storefront (never in admin-preview mode) — the admin recorder + registry-
 * side throttle keeps repeat calls per (component, session) idempotent.
 *
 * Endpoint: `POST {assetBaseUrl}/api/v1/storefront/components/{id}/view`
 * Auth:     `x-jscloud-tok` — reuses the same token the bundle already
 *           has for cloud component reads and asset writes.
 *
 * Failure is silent by design: this is telemetry, not user-visible. The
 * caller doesn't await — errors are swallowed inside so a throw can't
 * bubble into the boot sequence.
 */
import { resolveToken, TokenResolutionError } from './resolveToken';
import type { WebshopConfig } from './webshopConfig';

const DEFAULT_ASSET_BASE = 'admin.configento.app';

/** Sticky per-tab identifier — random UUID, kept for the whole tab lifetime.
 *  Doesn't leave the browser as-is; we send only its hashed form. The
 *  admin-side throttle key uses this + component_id to suppress repeat
 *  page-loads within 10 minutes. */
function getSessionHash(): string {
    const KEY = 'configento.session_id';
    try {
        let id = sessionStorage.getItem(KEY);
        if (id === null || id === '') {
            id = crypto.randomUUID();
            sessionStorage.setItem(KEY, id);
        }
        return id;
    } catch {
        // Private-mode Safari rejects sessionStorage. Fall back to a
        // per-boot random id — throttle becomes per-boot instead of
        // per-tab, acceptable degradation.
        return crypto.randomUUID();
    }
}

/**
 * Coarse device class from UA sniffing. NOT fingerprinting: we emit one
 * of the 4 buckets the webhook spec allows, everything else maps to
 * 'unknown'. Bot detection is intentionally shallow — the portal can
 * refine downstream.
 */
function detectDeviceClass(): 'desktop' | 'mobile' | 'tablet' | 'bot' | 'unknown' {
    if (typeof navigator === 'undefined') return 'unknown';
    const ua = (navigator.userAgent || '').toLowerCase();
    if (!ua) return 'unknown';
    if (/(bot|crawl|spider|slurp|googlebot|bingbot|duckduckbot|yandex|baiduspider|facebot|ahrefs|semrush)/.test(ua)) {
        return 'bot';
    }
    if (/ipad|tablet/.test(ua)) return 'tablet';
    if (/mobile|iphone|android/.test(ua)) return 'mobile';
    return 'desktop';
}

function extractReferrerDomain(): string | undefined {
    try {
        const ref = document.referrer;
        if (!ref) return undefined;
        const u = new URL(ref);
        // Same-origin referrer is noise, not intel — skip.
        if (u.host === window.location.host) return undefined;
        return u.host;
    } catch {
        return undefined;
    }
}

function viewEndpoint(assetBaseUrl: string | undefined, componentId: string): string {
    const base = (assetBaseUrl ?? DEFAULT_ASSET_BASE).replace(/^https?:\/\//, '').replace(/\/$/, '');
    return `https://${base}/api/v1/storefront/components/${encodeURIComponent(componentId)}/view`;
}

/** Preview-context shape resolveToken() wants. Same shim cloudAsset.ts uses. */
function configToPreviewContext(config: WebshopConfig) {
    return {
        componentId:     config.componentId,
        customer:        config.customer ?? '',
        token:           config.token ?? '',
        tokenEndpoint:   config.tokenEndpoint,
        cloudBaseUrl:    config.cloudBaseUrl ?? '',
        taxRate:         (config.taxRate as string) ?? '',
        frontendVersion: config.frontendVersion ?? '',
    };
}

/**
 * Send the view event. Returns a promise the caller CAN await for tests,
 * but the production callsite (ConfiguratorRoot.tsx) fires-and-forgets.
 * The internal try/catches guarantee no unhandled rejection at the call
 * site even under network failure.
 */
export async function fireComponentViewed(config: WebshopConfig): Promise<void> {
    // Called only when embedMode === 'storefront' — the caller checks
    // upstream so this file has no coupling to preview-context internals.
    try {
        const componentId = config.componentId;
        if (!componentId) return;

        let token: string;
        try {
            token = await resolveToken(configToPreviewContext(config));
        } catch (err) {
            if (err instanceof TokenResolutionError) return; // silent — no token, no telemetry
            throw err;
        }
        if (!token) return;

        const url = viewEndpoint(config.assetBaseUrl, componentId);
        const body: Record<string, unknown> = {
            url:          window.location.href,
            session_hash: getSessionHash(),
            device_class: detectDeviceClass(),
        };
        const ref = extractReferrerDomain();
        if (ref !== undefined) body.referrer_domain = ref;

        await fetch(url, {
            method: 'POST',
            mode:   'cors',
            credentials: 'omit',
            headers: {
                'Content-Type':  'application/json',
                'Accept':        'application/json',
                'X-Jscloud-Tok': token,
            },
            body: JSON.stringify(body),
            // keepalive lets the request continue even if the user
            // navigates away immediately after page-load — useful for
            // storefront pages that get closed fast.
            keepalive: true,
        });
        // Response ignored: 200 accepted / 200 throttled / 401 stale token
        // are all "we did our best". Retrying stale-token here would make
        // the boot sequence slower; the next page-load re-fires with a
        // fresher token anyway.
    } catch {
        // Explicit swallow — this is telemetry, MUST NOT crash the boot.
    }
}
